import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import './NavBar.css';
import logo from '../../logo.svg';
import { FiMenu, FiX } from 'react-icons/fi';

const NavBar = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const auth = getAuth();

  const handleLogoClick = () => {
    navigate('/');
  };

  const scrollToElement = (id) => {
    // If we're on the homepage, scroll to the element
    if (location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Otherwise, navigate to the homepage with a state parameter
      navigate('/', { state: { scrollTo: id } });
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleMobileClick = (action) => {
    closeMobileMenu();
    action();
  };

  const handleSignOut = () => {
    signOut(auth).then(() => {
      // Sign-out successful.
    }).catch((error) => {
      // An error happened.
      console.error('Sign out error', error);
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
    };
    
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  // Cleanup function to ensure body class is removed when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, []);

  if (user) {
    return (
      <nav className="dashboard-nav">
        <div className="nav-content">
          <NavLink to="/dashboard/block-management" className="nav-logo">
            <img src={logo} alt="Block Logo" />
          </NavLink>
          <div className="nav-menu-icon" onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </div>
          <div className={isMobileMenuOpen ? "dashboard-links open" : "dashboard-links"}>
            <NavLink to="/dashboard/block-management" className="dashboard-link" onClick={closeMobileMenu}>Block Management</NavLink>
            <NavLink to="/dashboard/sunscreen-management" className="dashboard-link" onClick={closeMobileMenu}>Sunscreen Management</NavLink>
            <NavLink to="/dashboard/settings" className="dashboard-link" onClick={closeMobileMenu}>Settings</NavLink>
            <button onClick={handleSignOut} className="sign-out-btn">Sign Out</button>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <div className="home-navbar navbar-container">
      <div className="max-width">
        <header data-thq="thq-navbar" className="home-navbar-interactive">
          <div 
            className="home-logo1 cursor-pointer" 
            onClick={handleLogoClick}
          >
            <img alt="image" src="/black_icon_only.png" className="home-image1" />
            <span 
              className="brandName cursor-pointer"
              onClick={handleLogoClick}
            >
                <span className="brand-orange">Sun</span>
              <span className="home-text11"> Blocks</span>
            </span>
          </div>
          <div data-thq="thq-navbar-nav" className="home-desktop-menu">
            <div className="home-links">
              <span 
                className="nav-link"
                onClick={() => navigate('/product')}
              >
                Product
              </span>
              
              <span 
                className="nav-link"
                onClick={() => navigate('/block-map')}
              >
                Locations
              </span>
              
              {/* About Us Dropdown */}
             
              
              <span 
                className="nav-link"
                onClick={() => navigate('/partners')}
              >
                Partners
              </span>
              <div className="dropdown-container">
                <span 
                  className="nav-link dropdown-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(!showDropdown);
                  }}
                >
                  About Us <span className="dropdown-arrow">▼</span>
                </span>
                {showDropdown && (
                  <div className="dropdown-menu">
                    <span 
                      className="dropdown-item"
                      onClick={() => {
                        navigate('/about');
                        setShowDropdown(false);
                      }}
                    >
                      About Us
                    </span>
                    <span 
                      className="dropdown-item"
                      onClick={() => {
                        scrollToElement('mission');
                        setShowDropdown(false);
                      }}
                    >
                      Our Mission
                    </span>
                    <span 
                      className="dropdown-item"
                      onClick={() => {
                        navigate('/sunscreen-facts');
                        setShowDropdown(false);
                      }}
                    >
                      Sunscreen Facts
                    </span>
                    <span 
                      className="dropdown-item"
                      onClick={() => {
                        navigate('/how-to-use');
                        setShowDropdown(false);
                      }}
                    >
                      How to Use
                    </span>
                    <span 
                      className="dropdown-item"
                      onClick={() => {
                        navigate('/block-map');
                        setShowDropdown(false);
                      }}
                    >
                      Locations
                    </span>
                    <span 
                      className="dropdown-item"
                      onClick={() => {
                        navigate('/contact');
                        setShowDropdown(false);
                      }}
                    >
                      Contact
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="home-buttons">
              <button 
                className="button-secondary button border-brand-dark-blue"
                onClick={() => navigate('/login')}
              >
                Log in
              </button>
            </div>
          </div>
          
          {/* Mobile hamburger menu button */}
          <div data-thq="thq-burger-menu" className="home-burger-menu" onClick={toggleMobileMenu}>
            <svg viewBox="0 0 1024 1024" className="home-icon10">
              <path d="M128 554.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 298.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 810.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667z"></path>
            </svg>
          </div>
        </header>
      </div>
      
      {/* Mobile menu - separate from header for better positioning */}
      <div className={`home-mobile-menu ${isMobileMenuOpen ? 'menu-open' : ''}`}>
        <div className="home-nav">
          <div className="home-top">
            <div className="home-logo2">
              <img alt="image" src="/black_icon_only.png" className="home-image2" />
              <span className="brandName">
                <span className="brand-orange">Sun</span>
                <span className="home-text17"> Blocks</span>
              </span>
            </div>
            <div data-thq="thq-close-menu" className="home-close-menu" onClick={toggleMobileMenu}>
              <svg viewBox="0 0 1024 1024" className="home-icon12">
                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
              </svg>
            </div>
          </div>
          
          {/* Main content area - nav links */}
          <div className="home-menu-content">
            <div className="home-links">
              <span 
                className="nav-link"
                onClick={() => handleMobileClick(() => navigate('/product'))}
              >
                Product
              </span>
              <span 
                className="nav-link"
                onClick={() => handleMobileClick(() => navigate('/block-map'))}
              >
                Locations
              </span>
              <span 
                className="nav-link"
                onClick={() => handleMobileClick(() => navigate('/about'))}
              >
                About Us
              </span>
              <span 
                className="nav-link"
                onClick={() => handleMobileClick(() => scrollToElement('mission'))}
              >
                Our Mission
              </span>
              <span 
                className="nav-link"
                onClick={() => handleMobileClick(() => navigate('/sunscreen-facts'))}
              >
                Sunscreen Facts
              </span>
              <span 
                className="nav-link"
                onClick={() => handleMobileClick(() => navigate('/how-to-use'))}
              >
                How to Use
              </span>
              <span 
                className="nav-link"
                onClick={() => handleMobileClick(() => navigate('/contact'))}
              >
                Contact
              </span>
              <span 
                className="nav-link"
                onClick={() => handleMobileClick(() => navigate('/partners'))}
              >
                Partners
              </span>
            </div>
          </div>
          
          {/* Footer area - buttons */}
          <div className="home-menu-footer">
            <div className="home-buttons">
              <button 
                className="button" 
                onClick={() => handleMobileClick(() => navigate('/login'))}
              >
                Log in
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavBar;